import React, { useState } from 'react';
import { Shield, Award, Clock, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { countryCodes } from '../mockData';

export const LandingPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    countryCode: '+1',
    contactNumber: '',
    message: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('LP Form submitted:', formData);
    alert('Thank you! Your request has been submitted. We will contact you soon.');
    setFormData({
      name: '',
      email: '',
      countryCode: '+1',
      contactNumber: '',
      message: ''
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section with Form */}
      <section className="relative bg-gradient-to-br from-orange-600 via-orange-500 to-red-600 py-20 overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1758519288480-1489c17b1519?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHw0fHxsZWdhbCUyMGJ1c2luZXNzfGVufDB8fHx8MTc2NDM2MzY4MXww&ixlib=rb-4.1.0&q=85"
            alt="Business consultation background"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-orange-600/75 via-orange-500/75 to-red-600/80"></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Side - Content */}
            <div className="text-white">
              <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
                Secure Your Brand Today
              </h1>
              <p className="text-xl md:text-2xl mb-8 text-orange-100">
                Professional trademark registration services starting at just $45. Protect your business identity with expert legal support.
              </p>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-start space-x-3">
                  <CheckCircle2 className="h-6 w-6 text-green-400 flex-shrink-0 mt-1" />
                  <span className="text-lg">Federal trademark protection across all 50 states</span>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle2 className="h-6 w-6 text-green-400 flex-shrink-0 mt-1" />
                  <span className="text-lg">Comprehensive trademark search included</span>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle2 className="h-6 w-6 text-green-400 flex-shrink-0 mt-1" />
                  <span className="text-lg">Dedicated case manager for your application</span>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle2 className="h-6 w-6 text-green-400 flex-shrink-0 mt-1" />
                  <span className="text-lg">98% success rate with fast processing</span>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-6 py-8 border-t border-b border-orange-700">
                <div className="text-center">
                  <div className="text-4xl font-bold mb-2">10K+</div>
                  <div className="text-orange-200">Trademarks Filed</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-bold mb-2">98%</div>
                  <div className="text-orange-200">Success Rate</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-bold mb-2">15+</div>
                  <div className="text-orange-200">Years Exp.</div>
                </div>
              </div>
            </div>

            {/* Right Side - Form */}
            <div>
              <Card className="p-8 shadow-2xl bg-white">
                <h2 className="text-3xl font-bold text-gray-900 mb-2">Get Started Now</h2>
                <p className="text-gray-600 mb-6">Fill out the form below and we'll contact you within 24 hours</p>
                
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div>
                    <Label htmlFor="lp-name">Name *</Label>
                    <Input
                      id="lp-name"
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="mt-1"
                      placeholder="Enter your full name"
                    />
                  </div>

                  <div>
                    <Label htmlFor="lp-email">Email *</Label>
                    <Input
                      id="lp-email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="mt-1"
                      placeholder="Enter your email"
                    />
                  </div>

                  <div>
                    <Label htmlFor="lp-contact">Contact Number *</Label>
                    <div className="flex gap-2 mt-1">
                      <Select
                        value={formData.countryCode}
                        onValueChange={(value) => setFormData({ ...formData, countryCode: value })}
                      >
                        <SelectTrigger className="w-32">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {countryCodes.map((item) => (
                            <SelectItem key={item.code} value={item.code}>
                              {item.code}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input
                        id="lp-contact"
                        type="tel"
                        required
                        value={formData.contactNumber}
                        onChange={(e) => {
                          const value = e.target.value.replace(/[^0-9]/g, '');
                          if (value.length <= 10) {
                            setFormData({ ...formData, contactNumber: value });
                          }
                        }}
                        placeholder="Phone number"
                        className="flex-1"
                        maxLength={10}
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="lp-message">Message</Label>
                    <Textarea
                      id="lp-message"
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      className="mt-1"
                      placeholder="Tell us about your trademark needs..."
                      rows={4}
                    />
                  </div>

                  <Button type="submit" className="w-full bg-orange-600 hover:bg-orange-700 py-6 text-lg font-semibold">
                    Submit Request
                  </Button>
                  
                  <p className="text-xs text-gray-500 text-center">
                    By submitting this form, you agree to our Privacy Policy and Terms of Service
                  </p>
                </form>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Why Choose Corporate Marking?</h2>
            <p className="text-xl text-gray-600">Trusted trademark services with proven results</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <Card className="p-8 text-center hover:shadow-xl transition-shadow">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-orange-100 text-orange-600 mb-6">
                <Shield className="h-8 w-8" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Expert Protection</h3>
              <p className="text-gray-600 leading-relaxed">
                Licensed attorneys with 15+ years of trademark law experience protecting businesses like yours
              </p>
            </Card>

            <Card className="p-8 text-center hover:shadow-xl transition-shadow">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-orange-100 text-orange-600 mb-6">
                <Clock className="h-8 w-8" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Fast Processing</h3>
              <p className="text-gray-600 leading-relaxed">
                Quick turnaround times with streamlined processes and expedited filing options available
              </p>
            </Card>

            <Card className="p-8 text-center hover:shadow-xl transition-shadow">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-orange-100 text-orange-600 mb-6">
                <Award className="h-8 w-8" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Proven Success</h3>
              <p className="text-gray-600 leading-relaxed">
                98% approval rate with thousands of successfully registered trademarks nationwide
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* Pricing Preview */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Affordable Plans for Every Business</h2>
            <p className="text-xl text-gray-600">Starting at just $45 + Federal Filing Fee</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            <Card className="p-6 text-center hover:shadow-lg transition-shadow">
              <div className="text-3xl font-bold text-orange-600 mb-2">$45</div>
              <div className="text-gray-600 mb-4">Essentials</div>
              <p className="text-sm text-gray-600">Perfect for basic trademark filing</p>
            </Card>

            <Card className="p-6 text-center hover:shadow-lg transition-shadow">
              <div className="text-3xl font-bold text-orange-600 mb-2">$149</div>
              <div className="text-gray-600 mb-4">Enhanced</div>
              <p className="text-sm text-gray-600">Direct hit search & alerts included</p>
            </Card>

            <Card className="p-6 text-center border-2 border-orange-600 hover:shadow-xl transition-shadow">
              <div className="text-xs bg-orange-600 text-white px-3 py-1 rounded-full inline-block mb-2">POPULAR</div>
              <div className="text-3xl font-bold text-orange-600 mb-2">$299</div>
              <div className="text-gray-600 mb-4">Deluxe</div>
              <p className="text-sm text-gray-600">Full protection & case manager</p>
            </Card>

            <Card className="p-6 text-center hover:shadow-lg transition-shadow">
              <div className="text-3xl font-bold text-orange-600 mb-2">$349</div>
              <div className="text-gray-600 mb-4">Platinum</div>
              <p className="text-sm text-gray-600">Premium service with priority</p>
            </Card>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-r from-orange-600 to-red-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">Don't Wait – Protect Your Brand Now</h2>
          <p className="text-xl mb-8 text-orange-100 max-w-2xl mx-auto">
            Every day without trademark protection is a risk. Secure your brand identity today with Corporate Marking.
          </p>
          <Button
            size="lg"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="bg-white text-orange-600 hover:bg-orange-50 text-lg px-8 py-6 font-semibold"
          >
            Get Started Now
          </Button>
        </div>
      </section>
    </div>
  );
};
